import React, { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Footer from '../components/Footer';
import Header from '../components/Header';
import { useCart } from '../context/CartContext';
import { Helmet } from 'react-helmet-async';
import { getPrimaryProductImage } from '../utils/productImages';

function Checkout() {
  const { state } = useLocation();
  const { clearCart } = useCart();

  const cartItemsFromState = state?.cartItems;
  const isCartMode = Array.isArray(cartItemsFromState) && cartItemsFromState.length > 0;
  const product = isCartMode ? null : state?.product;
  const quantity = isCartMode ? 1 : (state?.quantity || 1);

  const subtotal = isCartMode
    ? cartItemsFromState.reduce((s, i) => s + parseFloat(i.price) * i.quantity, 0)
    : (product ? parseFloat(product.price) * quantity : 0);

  const [fulfillment, setFulfillment] = useState('pickup');
  const [step, setStep] = useState('fulfillment');
  const [address, setAddress] = useState({ name: '', phone: '', street: '', city: '' });
  const [addrErrors, setAddrErrors] = useState({});

  const validateAddress = () => {
    const errors = {};
    if (!address.name.trim()) errors.name = 'Full name is required.';
    if (!address.phone.trim()) errors.phone = 'Phone number is required.';
    if (!address.street.trim()) errors.street = 'Delivery address is required.';
    if (!address.city.trim()) errors.city = 'City / Town is required.';
    setAddrErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const [method, setMethod] = useState('card');
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [mpesaStep, setMpesaStep] = useState('form');
  const [card, setCard] = useState({ name: '', number: '', expiry: '', cvv: '' });
  const [cardErrors, setCardErrors] = useState({});
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const formatCardNumber = val =>
    val.replace(/\D/g, '').slice(0, 16).replace(/(.{4})/g, '$1 ').trim();

  const formatExpiry = val => {
    const digits = val.replace(/\D/g, '').slice(0, 4);
    return digits.length > 2 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits;
  };

  const validateCard = () => {
    const errors = {};
    if (!card.name.trim()) errors.name = 'Cardholder name is required.';
    if (card.number.replace(/\s/g, '').length < 16) errors.number = 'Enter a valid 16-digit card number.';
    if (card.expiry.length < 5) errors.expiry = 'Enter expiry as MM/YY.';
    if (card.cvv.length < 3) errors.cvv = 'CVV must be 3-4 digits.';
    setCardErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validatePhone = () => {
    const cleaned = phone.replace(/\s/g, '');
    if (!/^(07|01|2547|2541)\d{8}$/.test(cleaned)) {
      setPhoneError('Enter a valid Kenyan phone number (e.g. 0712345678).');
      return false;
    }
    setPhoneError('');
    return true;
  };

  const handleCardPay = () => {
    if (!validateCard()) return;
    setProcessing(true);
    setTimeout(() => { setProcessing(false); if (isCartMode) clearCart(); setSuccess(true); }, 2500);
  };

  const handleMpesaPush = () => {
    if (!validatePhone()) return;
    setProcessing(true);
    setTimeout(() => { setProcessing(false); setMpesaStep('pin'); }, 2000);
  };

  const handleMpesaConfirm = () => {
    setProcessing(true);
    setTimeout(() => { setProcessing(false); if (isCartMode) clearCart(); setSuccess(true); }, 2000);
  };

  const steps = fulfillment === 'delivery'
    ? ['Fulfillment', 'Delivery', 'Payment']
    : ['Fulfillment', 'Payment'];
  const stepIndex = { fulfillment: 0, address: 1, payment: fulfillment === 'delivery' ? 2 : 1 };
  const currentStepIdx = stepIndex[step];

  if (!isCartMode && !product) {
    return (
      <div className="checkout-page">
        <Header />
        <div className="status-msg">No product selected. <Link to="/">Return to shop</Link></div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="checkout-page">
        <Header />
        <div className="checkout-success">
          <div className="success-icon">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h2>Order Confirmed!</h2>
          <p>
            {isCartMode
              ? (<>Thank you! <strong>{cartItemsFromState.length} item{cartItemsFromState.length !== 1 ? 's' : ''}</strong> totalling <strong>KES {subtotal.toLocaleString('en-KE', {minimumFractionDigits:2, maximumFractionDigits:2})}</strong>.</>)
              : (<>Thank you! <strong>{quantity} x {product.name}</strong> for <strong>KES {subtotal.toLocaleString('en-KE', {minimumFractionDigits:2, maximumFractionDigits:2})}</strong>.</>)
            }
          </p>
          {fulfillment === 'delivery'
            ? <p className="success-sub">We will contact you at <strong>{address.phone}</strong> to confirm your delivery details and fee.</p>
            : <p className="success-sub">Please visit our store to collect your order. We will have it ready!</p>
          }
          <Link to="/" className="order-btn" style={{ textDecoration: 'none', textAlign: 'center', marginTop: 24 }}>
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <Helmet>
        <title>Checkout – Hercles Global</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Header />

      <div className="checkout-container">

        {/* Order Summary */}
        <div className="checkout-summary">
          <h3 className="checkout-section-title">Order Summary</h3>

          {isCartMode ? (
            <div className="checkout-cart-items">
              {cartItemsFromState.map(item => {
                const img = getPrimaryProductImage(item);
                return (
                <div key={item.id} className="checkout-product-row">
                  {img && <img src={img} alt={item.name} className="checkout-product-img" />}
                  <div>
                    <p className="checkout-product-name">{item.name}</p>
                    <p className="checkout-product-qty">Qty: {item.quantity} &nbsp;&mdash;&nbsp; KES {(parseFloat(item.price) * item.quantity).toLocaleString('en-KE', {minimumFractionDigits:2, maximumFractionDigits:2})}</p>
                  </div>
                </div>
                );
              })}
            </div>
          ) : (
            <div className="checkout-product-row">
              {getPrimaryProductImage(product) && <img src={getPrimaryProductImage(product)} alt={product.name} className="checkout-product-img" />}
              <div>
                <p className="checkout-product-name">{product.name}</p>
                {product.category_name && <p className="checkout-product-cat">{product.category_name}</p>}
                <p className="checkout-product-qty">Qty: {quantity}</p>
              </div>
            </div>
          )}

          <div className="checkout-totals">
            <div className="checkout-total-row">
              <span>Subtotal</span><span>KES {subtotal.toLocaleString('en-KE', {minimumFractionDigits:2, maximumFractionDigits:2})}</span>
            </div>
            <div className="checkout-total-row">
              <span>Fulfillment</span>
              <span>{fulfillment === 'pickup' ? 'Store Pickup - Free' : 'Home Delivery'}</span>
            </div>
            {fulfillment === 'delivery' && (
              <div className="checkout-total-row delivery-fee-note">
                <span></span><span>Delivery fee confirmed after order</span>
              </div>
            )}
            <div className="checkout-total-row total">
              <span>Total</span><span>KES {subtotal.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Right Panel */}
        <div className="checkout-payment">

          {/* Stepper */}
          <div className="checkout-steps">
            {steps.map((label, i) => (
              <React.Fragment key={label}>
                <div className={`checkout-step${i < currentStepIdx ? ' done' : i === currentStepIdx ? ' active' : ''}`}>
                  <span className="step-num">{i < currentStepIdx ? '\u2713' : i + 1}</span>
                  <span className="step-label">{label}</span>
                </div>
                {i < steps.length - 1 && <div className={`step-line${i < currentStepIdx ? ' done' : ''}`} />}
              </React.Fragment>
            ))}
          </div>

          {/* Step: Fulfillment */}
          {step === 'fulfillment' && (
            <div className="payment-form">
              <p className="form-section-heading">How would you like to receive your order?</p>
              <div className="fulfillment-options">
                <button
                  className={`fulfillment-option${fulfillment === 'pickup' ? ' selected' : ''}`}
                  onClick={() => setFulfillment('pickup')}
                  type="button"
                >
                  <span className="fulfillment-label">Store Pickup</span>
                  <span className="fulfillment-sub">Collect from our store - Free</span>
                </button>
                <button
                  className={`fulfillment-option${fulfillment === 'delivery' ? ' selected' : ''}`}
                  onClick={() => setFulfillment('delivery')}
                  type="button"
                >
                  <span className="fulfillment-label">Home Delivery</span>
                  <span className="fulfillment-sub">We deliver to your doorstep - Fee varies</span>
                </button>
              </div>
              <button className="order-btn pay-btn" onClick={() => setStep(fulfillment === 'delivery' ? 'address' : 'payment')}>
                Continue
              </button>
            </div>
          )}

          {/* Step: Address (delivery only) */}
          {step === 'address' && (
            <div className="payment-form">
              <p className="form-section-heading">Delivery Details</p>
              <div className="form-group">
                <label>Full Name</label>
                <input type="text" placeholder="Jane Doe" value={address.name}
                  onChange={e => setAddress(a => ({ ...a, name: e.target.value }))}
                  className={addrErrors.name ? 'input-error' : ''} />
                {addrErrors.name && <span className="field-error">{addrErrors.name}</span>}
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                <input type="tel" placeholder="0712 345 678" value={address.phone}
                  onChange={e => setAddress(a => ({ ...a, phone: e.target.value }))}
                  className={addrErrors.phone ? 'input-error' : ''} />
                {addrErrors.phone && <span className="field-error">{addrErrors.phone}</span>}
              </div>
              <div className="form-group">
                <label>Delivery Address</label>
                <input type="text" placeholder="Street, building, apartment" value={address.street}
                  onChange={e => setAddress(a => ({ ...a, street: e.target.value }))}
                  className={addrErrors.street ? 'input-error' : ''} />
                {addrErrors.street && <span className="field-error">{addrErrors.street}</span>}
              </div>
              <div className="form-group">
                <label>City / Town</label>
                <input type="text" placeholder="Nairobi" value={address.city}
                  onChange={e => setAddress(a => ({ ...a, city: e.target.value }))}
                  className={addrErrors.city ? 'input-error' : ''} />
                {addrErrors.city && <span className="field-error">{addrErrors.city}</span>}
              </div>
              <div className="form-btn-row">
                <button className="back-step-btn" type="button" onClick={() => setStep('fulfillment')}>Back</button>
                <button className="order-btn pay-btn" onClick={() => { if (validateAddress()) setStep('payment'); }}>
                  Continue to Payment
                </button>
              </div>
            </div>
          )}

          {/* Step: Payment */}
          {step === 'payment' && (
            <>
              <div className={`addr-summary-chip${fulfillment === 'pickup' ? ' pickup' : ''}`}>
                <span>{fulfillment === 'delivery' ? `${address.street}, ${address.city}` : 'Store pickup selected'}</span>
                <button type="button" onClick={() => setStep(fulfillment === 'delivery' ? 'address' : 'fulfillment')}>Edit</button>
              </div>

              <p className="form-section-heading">Payment Method</p>
              <div className="payment-method-toggle">
                <button className={`method-btn${method === 'card' ? ' active' : ''}`} onClick={() => setMethod('card')}>
                  Card
                </button>
                <button className={`method-btn${method === 'mpesa' ? ' active' : ''}`} onClick={() => setMethod('mpesa')}>
                  M-Pesa
                </button>
              </div>

              {method === 'card' && (
                <div className="payment-form">
                  <div className="form-group">
                    <label>Cardholder Name</label>
                    <input type="text" placeholder="John Doe" value={card.name}
                      onChange={e => setCard({ ...card, name: e.target.value })}
                      className={cardErrors.name ? 'input-error' : ''} />
                    {cardErrors.name && <span className="field-error">{cardErrors.name}</span>}
                  </div>
                  <div className="form-group">
                    <label>Card Number</label>
                    <input type="text" placeholder="1234 5678 9012 3456" value={card.number}
                      onChange={e => setCard({ ...card, number: formatCardNumber(e.target.value) })}
                      className={cardErrors.number ? 'input-error' : ''} maxLength={19} />
                    {cardErrors.number && <span className="field-error">{cardErrors.number}</span>}
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Expiry</label>
                      <input type="text" placeholder="MM/YY" value={card.expiry}
                        onChange={e => setCard({ ...card, expiry: formatExpiry(e.target.value) })}
                        className={cardErrors.expiry ? 'input-error' : ''} maxLength={5} />
                      {cardErrors.expiry && <span className="field-error">{cardErrors.expiry}</span>}
                    </div>
                    <div className="form-group">
                      <label>CVV</label>
                      <input type="password" placeholder="***" value={card.cvv}
                        onChange={e => setCard({ ...card, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                        className={cardErrors.cvv ? 'input-error' : ''} maxLength={4} />
                      {cardErrors.cvv && <span className="field-error">{cardErrors.cvv}</span>}
                    </div>
                  </div>
                  <button className="order-btn pay-btn" onClick={handleCardPay} disabled={processing}>
                    {processing ? <span className="spinner" /> : `Pay KES ${subtotal.toFixed(2)}`}
                  </button>
                </div>
              )}

              {method === 'mpesa' && mpesaStep === 'form' && (
                <div className="payment-form">
                  <p className="mpesa-info">Enter your M-Pesa registered phone number. You will receive a payment prompt on your phone.</p>
                  <div className="form-group">
                    <label>Phone Number</label>
                    <input type="tel" placeholder="0712 345 678" value={phone}
                      onChange={e => setPhone(e.target.value)}
                      className={phoneError ? 'input-error' : ''} />
                    {phoneError && <span className="field-error">{phoneError}</span>}
                  </div>
                  <button className="order-btn pay-btn mpesa-btn" onClick={handleMpesaPush} disabled={processing}>
                    {processing ? <span className="spinner" /> : `Pay KES ${subtotal.toFixed(2)}`}
                  </button>
                </div>
              )}

              {method === 'mpesa' && mpesaStep === 'pin' && (
                <div className="payment-form mpesa-pin-step">
                  <div className="mpesa-prompt-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                      <line x1="12" y1="18" x2="12.01" y2="18" />
                    </svg>
                  </div>
                  <h3>Check Your Phone</h3>
                  <p>An M-Pesa prompt has been sent to <strong>{phone}</strong>. Enter your PIN to confirm payment of <strong>KES {subtotal.toFixed(2)}</strong>.</p>
                  <p className="mpesa-hint">Once you have entered your PIN, click the button below.</p>
                  <button className="order-btn pay-btn mpesa-btn" onClick={handleMpesaConfirm} disabled={processing}>
                    {processing ? <span className="spinner" /> : 'I Have Entered My PIN'}
                  </button>
                </div>
              )}
            </>
          )}

        </div>
      </div>
      <Footer />
    </div>
  );
}

export default Checkout;
